import Modal from '@common/modal';
import { useInterview } from '@components/pass/interview/start/context/interviewContext';
import TermsDto from '@domain/rs/terms/TermsRs';
import { useUserInfo } from '@repository/auth/useUserInfo';
import {
  useCheckInProgress,
  useCreateInterviewPractice,
  useDeleteInterviewPractice,
} from '@repository/interviewPractices/useInterviewPractices';
import { useTerms } from '@repository/terms/useTerms';
import { useCreateTermsAgree, useInterviewTermsCheckNew } from '@repository/userTermsAgrees/useUserTermsAgrees';
import useCheckAuthority from '@utils/hooks/useCheckAuthority';
import classnames from 'classnames/bind';
import { useState } from 'react';
import InterviewPracticeQuestionSelectLastModelBody from './body';
import InterviewPracticeQuestionSelectLastModalHeader from './header';
import style from './lastModal.module.scss';
import PrivacyPolicyAgreementModal from './privacyPolicyAgreementModal';

const cx = classnames.bind(style);

interface QuestionSelectLastModalProps {
  visible: boolean;
  onCloseClick: () => void;
}

const InterviewPracticeQuestionSelectLastModal = ({ visible, onCloseClick }: QuestionSelectLastModalProps) => {
  const { data: userInfo } = useUserInfo();
  const { isAuthorityUser } = useCheckAuthority(userInfo);
  const { data: progressInfo } = useCheckInProgress(isAuthorityUser);
  const { mutateAsync: deleteInterviewPractice } = useDeleteInterviewPractice();
  const { selectedQuestionList } = useInterview();
  const { mutateAsync: createInterviewPractice } = useCreateInterviewPractice();
  const { mutateAsync: fetchTermsCheckNew } = useInterviewTermsCheckNew();
  const { mutateAsync: createTermsAgree } = useCreateTermsAgree();
  const { data: termsList } = useTerms({ termType: 'INTERVIEW_PRIVACY_COLLECTION' });

  const [termsContents, setTermsContents] = useState<TermsDto | null>(null);
  const [termsSn, setTermsSn] = useState<number | null>(null);
  const [termsModalVisible, setTermsModalVisible] = useState(false);

  const openTermsModal = () => {
    setTermsModalVisible(true);
  };

  const closeTermsModal = () => {
    setTermsModalVisible(false);
  };

  const handleStartClick = async () => {
    // 진행중인 연습이 있다면 삭제
    if (progressInfo?.inProgress) await deleteInterviewPractice(progressInfo.interviewPracticeSn);

    // 약관동의 여부를 확인함
    const termsCheckData = await fetchTermsCheckNew();
    setTermsSn(termsCheckData.termsSn);
    const selectedTermsContents = termsList?.find((it) => it.sn === termsCheckData?.termsSn) ?? null;
    setTermsContents(selectedTermsContents);

    // 최근 약관에 동의하지않은 경우
    if (termsCheckData?.existsNewTermsAgree) {
      openTermsModal();
      return;
    }

    // 약관에 동의하여 인터뷰를 실행하는 경우
    const {
      data: { interviewPracticeSn },
    } = await createInterviewPractice(selectedQuestionList);

    if (!interviewPracticeSn) {
      return alert('면접 연습 생성에 실패하였습니다.');
    } else {
      window.open(`/pass/interview/practice?interviewPracticeSn=${interviewPracticeSn}`);
      onCloseClick();
    }
  };

  const handleAgreeClick = async () => {
    if (termsSn) await createTermsAgree(termsSn);
    await handleStartClick();
    onCloseClick();
  };

  return (
    <>
      <Modal visible={visible}>
        <div className={cx('layerLastModal')}>
          <InterviewPracticeQuestionSelectLastModalHeader
            questionCount={selectedQuestionList.length}
            onCloseClick={onCloseClick}
          />
          <div className={cx('divider')} />
          <InterviewPracticeQuestionSelectLastModelBody onStartClick={handleStartClick} onCancelClick={onCloseClick} />
        </div>
      </Modal>
      <PrivacyPolicyAgreementModal
        visible={termsModalVisible}
        termsData={termsContents}
        onClose={closeTermsModal}
        onOk={handleAgreeClick}
        isUseScrollHidden={false}
      />
    </>
  );
};

export default InterviewPracticeQuestionSelectLastModal;
